import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import {
  Shield, Swords, Target, Flame, Users, Cog, Crown, Loader2, BarChart3
} from 'lucide-react';
import TacticalShiftBanner from '../components/TacticalShiftBanner';

const CLASS_ICONS = {
  shield: Shield, swords: Swords, target: Target, flame: Flame,
  users: Users, cog: Cog, crown: Crown,
};

const CLASS_COLORS = {
  stoique: '#6366F1',
  avant_garde: '#EF4444',
  pilier: '#22C55E',
  stratege: '#3B82F6',
  phoenix: '#F97316',
  machine: '#A855F7',
  maitre_de_guerre: '#FFD700',
};

const TEAM_META = {
  rouge: { emoji: '🔴', name: 'Equipe Rouge', color: '#EF4444' },
  bleu: { emoji: '🔵', name: 'Equipe Bleu', color: '#3B82F6' },
};

export const ClassCensus = () => {
  const { language } = useLanguage();
  const [census, setCensus] = useState(null);
  const [loading, setLoading] = useState(true);

  const t = (fr, en) => (language === 'fr' ? fr : en);

  const fetchCensus = useCallback(async () => {
    try {
      const res = await api.get('/reports/mission/census');
      setCensus(res.data);
    } catch {
      // no data
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCensus(); }, [fetchCensus]);

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div></Layout>;
  }

  if (!census || census.total_reports === 0) {
    return (
      <Layout>
        <div className="max-w-lg mx-auto text-center py-16 px-4" data-testid="census-empty">
          <BarChart3 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-heading uppercase tracking-tight mb-2">
            {t('Recensement des Forces', 'Force Census')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('Aucun rapport genere ce mois-ci. Les classes apparaitront apres la generation des rapports mensuels.', 'No reports generated this month.')}
          </p>
        </div>
      </Layout>
    );
  }

  const dist = census.distribution || {};
  const sorted = Object.values(dist).sort((a, b) => b.percentage - a.percentage);
  const teams = census.team_analysis || {};
  const legendaries = census.legendaries || [];

  return (
    <Layout>
      <div className="max-w-2xl mx-auto pb-24 px-4" data-testid="class-census-page">
        {/* Tactical Shift Alert Banner */}
        <TacticalShiftBanner />
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-6 pt-4">
          <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            {t('Recensement des Forces', 'Force Census')}
          </p>
          <h1 className="text-2xl sm:text-3xl font-heading font-black uppercase tracking-tight text-foreground" data-testid="census-month-title">
            {census.month_label} {census.year}
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            {census.total_reports} {t('rapports generes', 'reports generated')}
          </p>
        </motion.div>

        {/* Legendary Hall — Maitres de Guerre */}
        {legendaries.length > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-6 p-5 rounded-2xl border-2 border-yellow-500/50 relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, rgba(250,204,21,0.1) 0%, transparent 60%)', boxShadow: '0 0 40px rgba(250,204,21,0.2)' }}
            data-testid="legendary-hall"
          >
            <div className="flex items-center gap-3 mb-3">
              <Crown className="w-6 h-6 text-yellow-400" />
              <h2 className="font-heading text-sm uppercase tracking-wider text-yellow-400">
                {t('Hall des Maitres de Guerre', 'War Master Hall')}
              </h2>
            </div>
            <div className="space-y-2">
              {legendaries.map((l, i) => {
                const teamMeta = TEAM_META[l.team_id] || {};
                return (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-yellow-500/5">
                    <Crown className="w-5 h-5 text-yellow-400" />
                    <span className="font-heading text-sm text-yellow-300">{l.name || 'Membre'}</span>
                    {teamMeta.emoji && <span className="text-sm">{teamMeta.emoji}</span>}
                    <span className="ml-auto text-xs text-yellow-500/60">{l.avg_score?.toFixed(1)}/10 avg</span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Class Distribution Bars */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-2xl border border-border/50 bg-card/30 p-5 mb-6"
          data-testid="class-distribution"
        >
          <h2 className="text-sm font-heading uppercase tracking-wider text-muted-foreground mb-4">
            {t('Composition de l\'Armee', 'Army Composition')}
          </h2>
          <div className="space-y-3">
            {sorted.map((cls, i) => {
              const color = CLASS_COLORS[cls.id] || '#888';
              const IconComp = CLASS_ICONS[cls.icon] || Shield;
              return (
                <motion.div
                  key={cls.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                  data-testid={`census-class-${cls.id}`}
                >
                  <div className="flex items-center gap-3 mb-1">
                    <IconComp className="w-4 h-4" style={{ color }} />
                    <span className="text-sm font-heading uppercase tracking-tight flex-1" style={{ color }}>
                      {cls.name}
                      {cls.legendary && <Crown className="w-3 h-3 inline ml-1 text-yellow-400" />}
                    </span>
                    <span className="text-sm font-black" style={{ color }}>{cls.percentage}%</span>
                    <span className="text-[10px] text-muted-foreground">({cls.count})</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted/20 overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ backgroundColor: color }}
                      initial={{ width: 0 }}
                      animate={{ width: `${cls.percentage}%` }}
                      transition={{ delay: 0.2 + i * 0.08, duration: 0.6 }}
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Team Analysis — Rouge vs Bleu */}
        {(teams.rouge?.total > 0 || teams.bleu?.total > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="rounded-2xl border border-border/50 bg-card/30 p-5 mb-6"
            data-testid="team-analysis"
          >
            <h2 className="text-sm font-heading uppercase tracking-wider text-muted-foreground mb-4">
              {t('Analyse d\'Armee — Clash des Meutes', 'Army Analysis — Pack Clash')}
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {['rouge', 'bleu'].map(teamId => {
                const meta = TEAM_META[teamId];
                const data = teams[teamId] || {};
                const domClass = COMBATANT_CLASSES_LOOKUP[data.dominant_class];
                return (
                  <div key={teamId} className="p-4 rounded-xl border" style={{ borderColor: `${meta.color}30`, background: `${meta.color}08` }} data-testid={`team-${teamId}`}>
                    <div className="text-center mb-3">
                      <span className="text-3xl">{meta.emoji}</span>
                      <p className="font-heading text-sm uppercase tracking-wider mt-1" style={{ color: meta.color }}>{meta.name}</p>
                      <p className="text-[10px] text-muted-foreground">{data.total || 0} {t('membres', 'members')}</p>
                    </div>
                    {data.dominant_class && (
                      <div className="text-center p-2 rounded-lg" style={{ backgroundColor: `${CLASS_COLORS[data.dominant_class] || '#888'}15` }}>
                        <p className="text-[9px] uppercase tracking-wider text-muted-foreground">{t('Classe Dominante', 'Dominant Class')}</p>
                        <p className="text-sm font-heading font-bold" style={{ color: CLASS_COLORS[data.dominant_class] || '#888' }}>
                          {data.dominant_class_name}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>
    </Layout>
  );
};

// Lookup for inline use
const COMBATANT_CLASSES_LOOKUP = {
  stoique: 'Le Stoique', avant_garde: "L'Avant-Garde", pilier: 'Le Pilier',
  stratege: 'Le Stratege', phoenix: 'Le Phoenix', machine: 'La Machine',
  maitre_de_guerre: 'Le Maitre de Guerre',
};

export default ClassCensus;
